/**
 * VideoEmbed — normalises YouTube / Vimeo / Loom / direct MP4 URLs into an
 * embeddable iframe (or <video>) player.
 */
import { PlayCircle } from "lucide-react";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

/**
 * Given a source URL return either {kind:'iframe', src, provider} for
 * YouTube/Vimeo/Loom embeds, or {kind:'video', src} for direct MP4/webm,
 * or {kind:'unknown'} if we can't determine the player type.
 */
export function parseVideoUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== "string") return { kind: "unknown" };
  const url = rawUrl.trim();

  // YouTube — watch?v=, youtu.be/, /embed/, /shorts/
  const ytWatch = url.match(/[?&]v=([A-Za-z0-9_-]{11})/);
  const ytShort = url.match(/youtu\.be\/([A-Za-z0-9_-]{11})/);
  const ytEmbed = url.match(/youtube\.com\/embed\/([A-Za-z0-9_-]{11})/);
  const ytShorts = url.match(/youtube\.com\/shorts\/([A-Za-z0-9_-]{11})/);
  const ytId = (ytWatch || ytShort || ytEmbed || ytShorts)?.[1];
  if (ytId) return { kind: "iframe", src: `https://www.youtube.com/embed/${ytId}?rel=0&modestbranding=1`, provider: "youtube" };

  // Vimeo
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return { kind: "iframe", src: `https://player.vimeo.com/video/${vimeo[1]}`, provider: "vimeo" };

  // Loom
  const loom = url.match(/loom\.com\/(?:share|embed)\/([a-f0-9]{20,})/);
  if (loom) return { kind: "iframe", src: `https://www.loom.com/embed/${loom[1]}`, provider: "loom" };

  // Direct media
  if (/\.(mp4|webm|ogg)(\?|$)/i.test(url)) return { kind: "video", src: url, provider: "direct" };

  return { kind: "unknown", src: url };
}

export default function VideoEmbed({ url, title = "", duration_seconds }) {
  const parsed = parseVideoUrl(url);

  if (parsed.kind === "iframe") {
    return (
      <div className="rounded-xl overflow-hidden bg-black aspect-video" data-testid="video-embed-iframe">
        <iframe
          src={parsed.src}
          title={title || "Training video"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
          className="w-full h-full block"
        />
      </div>
    );
  }

  if (parsed.kind === "video") {
    return (
      <div className="rounded-xl overflow-hidden bg-black aspect-video" data-testid="video-embed-native">
        <video src={parsed.src} title={title} controls preload="metadata" className="w-full h-full block" />
      </div>
    );
  }

  // Placeholder / unknown → link out
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="block rounded-xl aspect-video overflow-hidden group relative"
      style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #061633 100%)` }}
      data-testid="video-embed-unknown"
    >
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white group-hover:opacity-90 transition">
        <PlayCircle className="w-10 h-10" style={{ color: GOLD }} />
        <div className="text-sm font-medium">{title || "Open training video"}</div>
        <div className="text-[11px] font-mono text-white/60 truncate max-w-[80%]">{url}</div>
        {duration_seconds ? <div className="text-[10px] text-white/50 uppercase tracking-widest">{formatDuration(duration_seconds)}</div> : null}
      </div>
    </a>
  );
}

export function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return "";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
